import { TipoLogradouroPaisFields } from 'app/modules/apps/shared/endereco/TipoLogradouroPaisFields'
import { FC } from 'react'
import {
  ButtonType,
  IconPaths,
  IconSize,
  MaskType,
  OptionsHelper,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  LoadingIndicator,
  ListLoading,
  FormSelect,
  FormInput,
  FormDateInput,
  CustomAccordion,
  FormTextArea,
  ButtonIcon,
  Label,
} from '../../../../../../components'
import { usePacienteModal } from '../../hooks/usePacienteModal'
import { documentosPacienteRequests } from '../../core/_requests'
import { DocumentosPessoaForm } from 'app/modules/apps/shared/documentos-pessoa/DocumentosPessoaForm'
import { IdentificadoresPessoaFields } from 'app/modules/apps/shared/documentos-pessoa/IdentificadoresPessoaFields'
import { IInfo } from '../../../../../../components/Modal/SearchPersonModal'
import { useT } from '_metronic/i18n/Metronici18n'
import { OPCOES_JUSTIFICATIVA_NAO_POSSUI_CPF } from '../../core/_fciJustificativaCpf'

/**
 * Ids fixos dos combos e do checkbox novos.
 *
 * São **explícitos** (e não derivados de `name` dentro do `CustomSelect`) porque o `inputId` do
 * componente compartilhado é opt-in: derivá-lo automaticamente mudaria o DOM de todos os selects do
 * produto. O prefixo evita colisão com outros formulários abertos na mesma página.
 */
const idCampo = (campo: string) => `paciente-${campo}`

type Props = {
  info?: IInfo
}

export const PacienteModal: FC<Props> = ({ info }) => {
  const {
    formik,
    paciente,
    setDocumentosLista,
    documentoEstrangeiroNaLista,
    endereco,
    isAso,
    isMedico,
    showAso,
    showEndereco,
    setShowAso,
    setShowEndereco,
    isPacienteLoading,
    isEnderecoLoading,
    documentoAlterado,
    handleSubmit,
    closeModal,
    handleDeleteAlergia,
    handleDeleteDoenca,
    handleDeleteMedicamento,
    esusAtivo,
    camposObrigatoriosAgora,
    mostrarNaturalizacao,
    mostrarEstrangeiro,
    mostrarNascimentoBrasil,
    mostrarEtnia,
    mostrarJustificativaCpf,
    ufNascimento,
    resumoErro,
    registrarNacionalidades,
    registrarEstados,
    alternarDesconheceNomeMae,
    fetchOptionsRacas,
  } = usePacienteModal({ info })
  const { t } = useT()

  return (
    <>
      {info?.documento?.numero && (
        <div className='alert alert-warning'>
          <div className='alert-text font-weight-bold'>Paciente não possui cadastro.</div>
        </div>
      )}
      {resumoErro && (
        /*
          Resumo do 422 de cadastro incompleto. Renderizado como **texto React** — o React escapa o
          conteúdo por construção. Este caminho não passa por `showError`/`buildErrorMessage`, que
          injetam HTML não sanitizado no Swal (achado F6 da fatia F7c).
        */
        <div className='alert alert-danger' role='alert' aria-live='assertive'>
          <div className='alert-text fw-bold'>{resumoErro.mensagem}</div>
          {resumoErro.itens.length > 0 && (
            <ul className='mb-0 mt-2'>
              {resumoErro.itens.map((item) => (
                <li key={item.rotulo}>
                  <span className='fw-bold'>{item.rotulo}: </span>
                  <span>{item.correcao}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {paciente?.prontuario && (
        <p id={idCampo('prontuario')} className='fs-6 fw-bolder mb-3'>
          {t('cols.prontuario')}: {paciente.prontuario}
        </p>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <FormSelect
          formik={formik}
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          name='empresa'
          entity='empresa'
          isClearable={false}
          isRequired
          className='mb-3'
          isDisabled={!!paciente}
        />

        <div className='row mb-3'>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='primeiroNome'
              label='Primeiro Nome'
              placeholder='Digite o primeiro nome'
              isRequired
            />
          </div>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='sobreNome'
              label='Sobrenome'
              placeholder='Digite o sobrenome'
              isRequired
            />
          </div>
        </div>
        <div className='row mb-3'>
          <div className='col-md-6'>
            <FormInput formik={formik} name='email' label='E-mail' placeholder='Digite o e-mail' />
          </div>
          <div className='col-md-6'>
            {/*
              Telefone unificado: um só campo no cadastro do paciente. O valor vai para as duas
              colunas do backend — `pessoa.telefone`, que notificação/prescrição/Memed/Hilab e
              relatórios leem, e `pessoa.telefoneCelular`, que a Ficha de Cadastro Individual exige.
            */}
            <FormInput
              formik={formik}
              name='telefoneCelular'
              label='Telefone celular'
              placeholder='(00) 0 0000-0000'
              mask={MaskType.Phone}
              isRequired={camposObrigatoriosAgora.has('telefoneCelular')}
              infoText='Exigido pela Ficha de Cadastro Individual do e-SUS para brasileiro e naturalizado.'
            />
          </div>
        </div>
        {mostrarJustificativaCpf && (
          <div className='row mb-3'>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='justificativaNaoPossuiCpf'
                inputId={idCampo('justificativaNaoPossuiCpf')}
                label='Motivo de não possuir CPF'
                placeholder='Selecione o motivo'
                options={OPCOES_JUSTIFICATIVA_NAO_POSSUI_CPF}
                isDisabled={formik.isSubmitting}
                isRequired={camposObrigatoriosAgora.has('justificativaNaoPossuiCpf')}
              />
            </div>
          </div>
        )}
        <IdentificadoresPessoaFields
          formik={formik}
          prefixo=''
          cpf={formik.values.cpf}
          cns={formik.values.cns}
          documentoEstrangeiroNaLista={documentoEstrangeiroNaLista}
          idPrefix='paciente'
        />
        {documentoAlterado && (
          <FormTextArea
            formik={formik}
            name='justificativa'
            entity='justificativa_cpf_cns'
            placeholder='Descreva o motivo da alteração do CPF/CNS'
            className='mb-3'
            isRequired
          />
        )}
        <div className='row mb-3'>
          <div className='col-md-6'>
            <FormSelect
              formik={formik}
              options={OptionsHelper.generos}
              name='sexo'
              label='Sexo'
              placeholder='Selecione um sexo'
              isClearable={false}
              isDisabled={formik.isSubmitting}
              isRequired
            />
          </div>
          <div className='col-md-6'>
            <FormDateInput
              formik={formik}
              placeholder='Data de Nascimento'
              name='nascimento'
              label='Data de Nascimento'
              maxDate={new Date()}
              isRequired
            />
          </div>
        </div>

        {isMedico && (
          <>
            <CustomAccordion title='Alergias'>
              <div className='row my-2'>
                <h2>Alergias</h2>
              </div>
              {formik.values.alergias.length === 0 && (
                <div className='text-muted fs-7 mb-3'>Nenhuma alergia cadastrada</div>
              )}
              {formik.values.alergias.map((alergia, index) => (
                <div key={alergia.id ?? index} className='d-flex align-items-center gap-2 mb-3'>
                  <FormInput
                    formik={formik}
                    name={`alergias.${index}.descricao`}
                    placeholder='Descrição da alergia'
                    isDisabled={formik.isSubmitting || !!formik.values.alergias[index].id}
                    className='w-100'
                  />
                  <ButtonIcon
                    icon={IconPaths.Trash}
                    iconSize={IconSize.Small}
                    onClick={() => handleDeleteAlergia(index)}
                    className='p-0'
                  />
                </div>
              ))}
              <div className='d-flex flex-end'>
                <Button
                  className='btn-primary'
                  size='btn-sm'
                  icon={IconPaths.Add}
                  onClick={() => {
                    formik.setFieldValue('alergias', [...formik.values.alergias, { descricao: '' }])
                  }}
                >
                  {t('common.add')}
                </Button>
              </div>
            </CustomAccordion>
            <CustomAccordion title='Doenças crônicas'>
              <div className='row my-2'>
                <h2>Doenças crônicas</h2>
              </div>
              {formik.values.doencas.length === 0 && (
                <div className='text-muted fs-7 mb-3'>Nenhuma doença crônica cadastrada</div>
              )}
              {formik.values.doencas.map((doenca, index) => (
                <div key={doenca.id ?? index} className='d-flex align-items-center gap-2 mb-3'>
                  <FormInput
                    formik={formik}
                    name={`doencas.${index}.descricao`}
                    placeholder='Descrição da doença crônica'
                    className='w-100'
                    isDisabled={formik.isSubmitting || !!formik.values.doencas[index].id}
                  />
                  <ButtonIcon
                    icon={IconPaths.Trash}
                    iconSize={IconSize.Small}
                    onClick={() => handleDeleteDoenca(index)}
                    className='p-0'
                  />
                </div>
              ))}
              <div className='d-flex flex-end'>
                <Button
                  className='btn-primary'
                  size='btn-sm'
                  icon={IconPaths.Add}
                  onClick={() => {
                    formik.setFieldValue('doencas', [...formik.values.doencas, { descricao: '' }])
                  }}
                >
                  {t('common.add')}
                </Button>
              </div>
            </CustomAccordion>
            <CustomAccordion title='Medicamentos'>
              <div className='row my-2'>
                <h2>Medicamentos</h2>
              </div>
              {formik.values.medicamentos.length === 0 && (
                <div className='text-muted fs-7 mb-3'>Nenhum medicamento cadastrado</div>
              )}
              {formik.values.medicamentos.map((doenca, index) => (
                <div key={doenca.id ?? index} className='d-flex align-items-center gap-2 mb-3'>
                  <FormInput
                    formik={formik}
                    name={`medicamentos.${index}.descricao`}
                    placeholder='Medicamento'
                    className='w-100'
                    isDisabled={formik.isSubmitting || !!formik.values.medicamentos[index].id}
                  />
                  <ButtonIcon
                    icon={IconPaths.Trash}
                    iconSize={IconSize.Small}
                    onClick={() => handleDeleteMedicamento(index)}
                    className='p-0'
                  />
                </div>
              ))}
              <div className='d-flex flex-end'>
                <Button
                  className='btn-primary'
                  size='btn-sm'
                  icon={IconPaths.Add}
                  onClick={() => {
                    formik.setFieldValue('medicamentos', [
                      ...formik.values.medicamentos,
                      { descricao: '' },
                    ])
                  }}
                >
                  {t('common.add')}
                </Button>
              </div>
            </CustomAccordion>
          </>
        )}

        <div className='row my-5'>
          <h2>{t('documento_pessoa.titulo')}</h2>
        </div>
        <DocumentosPessoaForm
          ownerId={paciente?.id ? Number(paciente.id) : undefined}
          recurso='paciente'
          requests={documentosPacienteRequests}
          onChange={setDocumentosLista}
        />

        <CustomAccordion title='Informações adicionais'>
          <div className='row my-2'>
            <h2>Informações Adicionais</h2>
          </div>
          <FormInput
            formik={formik}
            name='nomeSocial'
            label='Nome Social'
            placeholder='Digite o nome social'
            className='mb-3'
          />
          <div className='row mb-3'>
            <div className='col-md-6'>
              <FormInput
                formik={formik}
                name='nomeMae'
                label='Nome da Mãe'
                placeholder='Digite o nome da mãe'
                isDisabled={formik.values.desconheceNomeMae}
                isRequired={camposObrigatoriosAgora.has('nomeMae')}
              />
              {/*
                Checkbox local (e não o `CheckBox` compartilhado, que não associa rótulo ao input):
                marcar desabilita e limpa o nome da mãe, desmarcar devolve o campo.
              */}
              <div className='form-check form-check-sm form-check-custom form-check-solid mt-2'>
                <input
                  id={idCampo('desconheceNomeMae')}
                  name='desconheceNomeMae'
                  className='form-check-input me-2'
                  type='checkbox'
                  checked={formik.values.desconheceNomeMae}
                  disabled={formik.isSubmitting}
                  onChange={(evento) => alternarDesconheceNomeMae(evento.target.checked)}
                />
                <Label htmlFor={idCampo('desconheceNomeMae')}>Nome da mãe desconhecido</Label>
              </div>
            </div>
            <div className='col-md-6'>
              <FormInput
                formik={formik}
                name='nomeRepresentante'
                label='Nome do representante'
                placeholder='Digite o nome do representante'
              />
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='raca'
                inputId={idCampo('raca')}
                label='Raça/Cor'
                placeholder='Selecione uma raça'
                fetchOptions={fetchOptionsRacas}
                // Muda a chave de busca do `CustomSelect` quando o filtro do e-SUS muda, para que a
                // lista seja recarregada sem "Não informado" assim que as regras chegam.
                fetchOptionsParams={{ esusAtivo }}
                isDisabled={formik.isSubmitting}
                isRequired={camposObrigatoriosAgora.has('raca')}
              />
            </div>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='genero'
                inputId={idCampo('genero')}
                label='Gênero'
                placeholder='Selecione um gênero'
                fetchOptions={OptionsHelper.getOptionsGeneros}
                isDisabled={formik.isSubmitting}
              />
            </div>
          </div>
          {mostrarEtnia && (
            <div className='row mb-3'>
              <div className='col-md-6'>
                <FormSelect
                  formik={formik}
                  name='etnia'
                  inputId={idCampo('etnia')}
                  label='Etnia'
                  placeholder='Selecione uma etnia'
                  fetchOptions={OptionsHelper.getOptionsEtnias}
                  isDisabled={formik.isSubmitting}
                  isRequired={camposObrigatoriosAgora.has('etnia')}
                />
              </div>
            </div>
          )}
          <div className='row mb-3'>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='nacionalidade'
                inputId={idCampo('nacionalidade')}
                label='Nacionalidade'
                placeholder='Selecione uma nacionalidade'
                fetchOptions={OptionsHelper.getOptionsNacionalidades}
                onOptionsLoaded={registrarNacionalidades}
                isDisabled={formik.isSubmitting}
                isRequired={camposObrigatoriosAgora.has('nacionalidade')}
              />
            </div>
            {mostrarNascimentoBrasil && (
              <div className='col-md-6'>
                <FormSelect
                  formik={formik}
                  name='estadoNascimento'
                  inputId={idCampo('estadoNascimento')}
                  label='Estado do Nascimento'
                  placeholder='Selecione um estado'
                  fetchOptions={OptionsHelper.getOptionsEstadosUfNascimento}
                  onOptionsLoaded={registrarEstados}
                  isDisabled={formik.isSubmitting}
                />
              </div>
            )}
          </div>
          {mostrarNascimentoBrasil && (
            <div className='row mb-3'>
              <div className='col-md-6'>
                {/*
                  Combo dependente da UF: sem estado selecionado fica desabilitado e vazio, e o
                  helper nem chama `GET /municipios/uf/{uf}`.
                */}
                <FormSelect
                  formik={formik}
                  name='municipioNascimento'
                  inputId={idCampo('municipioNascimento')}
                  label='Município de nascimento'
                  placeholder={ufNascimento ? 'Selecione um município' : 'Selecione antes o estado'}
                  fetchOptions={OptionsHelper.getOptionsMunicipiosPorUf}
                  fetchOptionsParams={{ uf: ufNascimento }}
                  isDisabled={formik.isSubmitting || !ufNascimento}
                  isRequired={camposObrigatoriosAgora.has('municipioNascimento')}
                />
              </div>
            </div>
          )}
          {mostrarNaturalizacao && (
            <div className='row mb-3'>
              <div className='col-md-6'>
                <FormDateInput
                  formik={formik}
                  name='dtNaturalizacao'
                  label='Data de naturalização'
                  placeholder='Data de naturalização'
                  maxDate={new Date()}
                  isDisabled={formik.isSubmitting}
                  isRequired={camposObrigatoriosAgora.has('dtNaturalizacao')}
                />
              </div>
              <div className='col-md-6'>
                <FormInput
                  formik={formik}
                  name='portariaNaturalizacao'
                  label='Portaria de naturalização'
                  placeholder='Digite o número da portaria'
                  isRequired={camposObrigatoriosAgora.has('portariaNaturalizacao')}
                />
              </div>
            </div>
          )}
          {mostrarEstrangeiro && (
            <div className='row mb-3'>
              <div className='col-md-6'>
                <FormSelect
                  formik={formik}
                  name='paisNascimento'
                  inputId={idCampo('paisNascimento')}
                  label='País de nascimento'
                  placeholder='Selecione um país'
                  fetchOptions={OptionsHelper.getOptionsPaises}
                  isDisabled={formik.isSubmitting}
                  isRequired={camposObrigatoriosAgora.has('paisNascimento')}
                />
              </div>
              <div className='col-md-6'>
                <FormDateInput
                  formik={formik}
                  name='dtEntradaBrasil'
                  label='Data de entrada no Brasil'
                  placeholder='Data de entrada no Brasil'
                  maxDate={new Date()}
                  isDisabled={formik.isSubmitting}
                  isRequired={camposObrigatoriosAgora.has('dtEntradaBrasil')}
                />
              </div>
            </div>
          )}
          {isMedico && (
            <>
              <div className='row mb-3'>
                <div className='col-md-6'>
                  <FormInput
                    formik={formik}
                    name='peso'
                    label='Peso'
                    placeholder='Digite um peso'
                    isDisabled={formik.isSubmitting}
                    type='number'
                    infoText='Peso em kg'
                  />
                </div>
                <div className='col-md-6'>
                  <FormInput
                    formik={formik}
                    name='altura'
                    label='Altura'
                    placeholder='Digite uma altura'
                    isDisabled={formik.isSubmitting}
                    type='number'
                    infoText='Altura em metros'
                  />
                </div>
              </div>
              <FormTextArea
                formik={formik}
                name='condicoes'
                label='Condições de saúde do paciente'
                placeholder='Digite as condições de saúde do paciente'
                isDisabled={formik.isSubmitting}
              />
            </>
          )}
        </CustomAccordion>

        {isAso && showAso && (
          <>
            <div className='row my-2'>
              <h2>Carteira de Identidade</h2>
            </div>
            <div className='row mb-3'>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='rg.numero'
                  label='Número'
                  placeholder='Digite o número'
                  mask={MaskType.RG}
                  isRequired
                />
              </div>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='rg.orgao'
                  label='Órgão'
                  placeholder='Digite o órgão'
                  isRequired
                />
              </div>
              <div className='col-md-4'>
                <FormDateInput
                  formik={formik}
                  placeholder='Data expedição'
                  name='rg.data'
                  label='Data expedição'
                  maxDate={new Date()}
                />
              </div>
            </div>

            <div className='row my-2'>
              <h2>Carteira de Trabalho</h2>
            </div>
            <div className='row mb-3'>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='ctps.numero'
                  label='Número'
                  placeholder='Digite o número'
                  mask={MaskType.CTPS_Numero}
                  isRequired
                />
              </div>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='ctps.serie'
                  label='Série'
                  placeholder='Digite o número de série'
                  mask={MaskType.CTPS_Serie}
                  isRequired
                />
              </div>
              <div className='col-md-4'>
                <FormSelect
                  formik={formik}
                  fetchOptions={OptionsHelper.getOptionsEstadosUf}
                  name='ctps.uf'
                  label='Estado'
                  placeholder='Selecione um estado'
                  isClearable={false}
                  isDisabled={formik.isSubmitting}
                  isRequired
                />
              </div>
            </div>

            <div className='row my-2'>
              <h2>Dados empresariais</h2>
            </div>
            <div className='row mb-3'>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='dadosAso.matricula'
                  label='Matrícula'
                  placeholder='Digite a matrícula'
                  mask={MaskType.Matricula}
                />
              </div>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='dadosAso.cargo'
                  label='Cargo'
                  placeholder='Digite o cargo'
                  isRequired
                />
              </div>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='dadosAso.setor'
                  label='Setor'
                  placeholder='Digite o setor'
                  isRequired
                />
              </div>
            </div>
          </>
        )}
        {isAso && (
          <div className='row my-4'>
            <div className='d-flex col-lg-4 ms-auto'>
              <Button
                className='btn-primary w-100'
                onClick={() => setShowAso((prev) => !prev)}
                disabled={isEnderecoLoading || isPacienteLoading}
              >
                {paciente?.dadosAso
                  ? showAso
                    ? 'Esconder Dados Aso'
                    : 'Ver Dados Aso'
                  : showAso
                  ? 'Remover Dados Aso'
                  : 'Adicionar Dados Aso'}
              </Button>
            </div>
          </div>
        )}

        {showEndereco && (
          <>
            <div className='row my-2'>
              <h2>Endereço</h2>
            </div>
            <TipoLogradouroPaisFields
              formik={formik}
              prefixo='endereco.'
              novo={!endereco}
              idPrefix='paciente-endereco'
            />
            <div className='row mb-3'>
              <div className='col-md-8'>
                <FormInput
                  formik={formik}
                  name='endereco.logradouro'
                  label='Logradouro'
                  placeholder='Digite o logradouro'
                  isRequired
                />
              </div>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='endereco.numero'
                  label='Número'
                  placeholder='Digite o número'
                  isRequired
                />
              </div>
            </div>
            <div className='row mb-3'>
              <div className='col-md-8'>
                <FormInput
                  formik={formik}
                  name='endereco.complemento'
                  label='Complemento'
                  placeholder='Digite o complemento'
                />
              </div>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='endereco.cep'
                  label='CEP'
                  mask={MaskType.CEP}
                  placeholder='00.000-000'
                  isRequired
                />
              </div>
            </div>
            <div className='row mb-3'>
              <div className='col-md-8'>
                <FormInput
                  formik={formik}
                  name='endereco.bairro'
                  label='Bairro'
                  placeholder='Digite o bairro'
                  isRequired
                />
              </div>
              <div className='col-md-4'>
                <FormInput
                  formik={formik}
                  name='endereco.cidade'
                  label='Cidade'
                  placeholder='Digite o cidade'
                  isRequired
                />
              </div>
            </div>

            <FormSelect
              formik={formik}
              fetchOptions={OptionsHelper.getOptionsEstadosUf}
              name='endereco.uf'
              label='Estado'
              placeholder='Selecione um estado'
              isClearable={false}
              isRequired
              className='mb-3'
            />
          </>
        )}
        {formik.values.empresa && (
          <div className='row my-4'>
            <div className='d-flex col-lg-4 ms-auto'>
              <Button
                className='btn-primary w-100'
                onClick={() => setShowEndereco((prev) => !prev)}
                disabled={isEnderecoLoading || isPacienteLoading}
              >
                {endereco
                  ? showEndereco
                    ? 'Esconder endereço'
                    : 'Ver endereço'
                  : showEndereco
                  ? 'Remover endereço'
                  : 'Adicionar endereço'}
              </Button>
            </div>
          </div>
        )}

        <div className='d-flex justify-content-center mt-10'>
          <Button
            type={ButtonType.Reset}
            onClick={closeModal}
            className='btn-secondary me-3'
            disabled={formik.isSubmitting}
          >
            {t('common.cancel')}
          </Button>

          <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
            {formik.isSubmitting ? (
              <LoadingIndicator />
            ) : (
              <span className='indicator-label'>{t('common.send')}</span>
            )}
          </Button>
        </div>

        {formik.isSubmitting && <ListLoading />}
      </form>
    </>
  )
}
